import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { openCalendlyPopup } from "@/lib/calendly";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/lib/i18n";
import PageHeader from "@/components/PageHeader";
import DailyQuoteCard from "@/components/DailyQuoteCard";
import QolCheckInModal from "@/components/QolCheckInModal";
import { Button } from "@/components/ui/button";
import { brainTypeMeta } from "@/lib/brainTypeGraphics";
import {
  Brain,
  HeartPulse,
  MessagesSquare,
  ArrowRight,
  Smile,
  Meh,
  Frown,
  Clock,
  Compass,
  MessageCircle,
  Heart,
  Sparkles,
  ShieldCheck,
  CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";

const QOL_SUBJECTS = [
  { key: "feel", labelKey: "qol.subject.feel", icon: Heart },
  { key: "past", labelKey: "qol.subject.past", icon: Clock },
  { key: "future", labelKey: "qol.subject.future", icon: Compass },
  { key: "inner_self_talk", labelKey: "qol.subject.inner_self_talk", icon: MessageCircle },
];

const QOL_OPTION_META = {
  good: { icon: Smile, color: "#4A5D4E", labelKey: "qol.option.good" },
  neutral: { icon: Meh, color: "#B37A5F", labelKey: "qol.option.neutral" },
  bad: { icon: Frown, color: "#9C6A53", labelKey: "qol.option.bad" },
};

/**
 * User Dashboard — the post-login landing page for client accounts.
 *
 * Four cards:
 *   1. Daily inspirational quote (blurred while the daily check-in modal is open)
 *   2. Brain Type (definitive from coach, preliminary from quick test, or CTA)
 *   3. Quality of Life summary (current 4 toggle states as pills)
 *   4. GOT Practice counter (total + completed)
 *
 * Also owns the once-per-day QoL check-in modal so users are gently
 * prompted to update their toggles before revealing the quote.
 */
export default function Dashboard() {
  const { user } = useAuth();
  const { t } = useT();

  const [qol, setQol] = useState({
    feel: null,
    past: null,
    future: null,
    inner_self_talk: null,
    updated_at: null,
  });
  const [gotStats, setGotStats] = useState({ total: 0, completed: 0 });
  const [pendingBtReq, setPendingBtReq] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCheckIn, setShowCheckIn] = useState(false);
  const [requestingBt, setRequestingBt] = useState(false);

  const requestBrainTypeCheck = async () => {
    setRequestingBt(true);
    try {
      const isDoubleCheck = user?.preliminary_brain_type != null;
      const { data } = await api.post("/qol/me/request-coaching", {
        reason: isDoubleCheck ? "brain_type_double_check" : "brain_type_consultation",
        note: isDoubleCheck
          ? `Client requests a double-check to confirm brain type. Quick-test result: #${user.preliminary_brain_type}.`
          : "Client requests a consultation to determine their brain type.",
      });
      // Mirror the returned request so the pending badge shows immediately.
      setPendingBtReq({
        id: data.request_id,
        status: "pending",
        reason: data.reason,
        created_at: data.created_at,
      });
      toast.success(t("bt_test.toast.requested"));
      // Open the coach's Calendly so the client can book the meeting.
      openCalendlyPopup(
        user?.coach_calendly_embed_code || user?.coach_calendly_url
      );
    } catch (e) {
      toast.error(
        formatApiErrorDetail(e.response?.data?.detail) ||
          t("bt_test.toast.could_not_send")
      );
    } finally {
      setRequestingBt(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [qolRes, gotRes, btReqRes] = await Promise.all([
          api.get("/qol/me"),
          api.get("/got/sessions/count"),
          api.get("/qol/me/pending-brain-type-request"),
        ]);
        if (cancelled) return;
        setQol({
          feel: qolRes.data.feel ?? null,
          past: qolRes.data.past ?? null,
          future: qolRes.data.future ?? null,
          inner_self_talk: qolRes.data.inner_self_talk ?? null,
          updated_at: qolRes.data.updated_at ?? null,
        });
        setGotStats(gotRes.data || { total: 0, completed: 0 });
        setPendingBtReq(btReqRes.data || null);
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Trigger the daily QoL check-in modal once per user per day.
  useEffect(() => {
    if (loading) return;
    if (!user) return;
    const today = new Date().toISOString().slice(0, 10);
    try {
      if (localStorage.getItem(`qol_checkin_last_${user.id}`) !== today) {
        setShowCheckIn(true);
      }
    } catch {
      // localStorage may be blocked in iframe contexts — silently skip.
    }
  }, [loading, user]);

  const dismissCheckIn = () => {
    setShowCheckIn(false);
    try {
      const today = new Date().toISOString().slice(0, 10);
      localStorage.setItem(`qol_checkin_last_${user.id}`, today);
    } catch {
      // ignore
    }
  };

  const definitiveBt = user?.brain_type ?? null;
  const preliminaryBt = user?.preliminary_brain_type ?? null;
  const btMeta = brainTypeMeta(definitiveBt ?? preliminaryBt);

  const formatDate = (iso) => {
    if (!iso) return null;
    try {
      const d = new Date(iso);
      return d.toLocaleString(undefined, {
        dateStyle: "medium",
        timeStyle: "short",
      });
    } catch {
      return iso;
    }
  };

  const hasAnyQol =
    qol.feel || qol.past || qol.future || qol.inner_self_talk;

  return (
    <div className="px-8 lg:px-12 py-12 max-w-6xl" data-testid="dashboard-page">
      <PageHeader
        overline={user?.name?.split(" ")[0] || ""}
        title={t("dashboard.title")}
        description={t("dashboard.description")}
      />

      {loading ? (
        <p className="text-sm text-[#8A9188] uppercase tracking-[0.2em] font-bold">
          {t("common.loading")}
        </p>
      ) : (
        <div className="space-y-8">
          {/* 1) Daily quote — full width, blurred while modal is up */}
          <DailyQuoteCard blurred={showCheckIn} />

          {/* 2 & 3 & 4 — three summary cards */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Brain Type */}
            <div
              className="bg-white border border-[#E5E2DC] rounded-2xl p-6 flex flex-col"
              data-testid="dashboard-brain-type-card"
            >
              <div className="flex items-center gap-3 mb-5">
                <div className="h-11 w-11 rounded-xl bg-[#F2F0EA] flex items-center justify-center">
                  <Brain strokeWidth={1.5} className="text-[#B37A5F]" size={20} />
                </div>
                <h2 className="font-heading text-xl text-[#1A1D1A] tracking-tight font-semibold">
                  {t("dashboard.bt.title")}
                </h2>
              </div>

              {definitiveBt != null ? (
                <div data-testid="dashboard-bt-definitive">
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#4A5D4E] flex items-center gap-1.5">
                    <ShieldCheck strokeWidth={1.5} size={14} />
                    {t("dashboard.bt.definitive")}
                  </p>
                  <p className="font-heading text-4xl text-[#1A1D1A] font-semibold mt-2">
                    #{definitiveBt}
                  </p>
                  {btMeta && (
                    <p className="text-sm text-[#5C635A] mt-1">{btMeta.name}</p>
                  )}
                </div>
              ) : preliminaryBt != null ? (
                <div data-testid="dashboard-bt-preliminary">
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#B37A5F]">
                    {pendingBtReq
                      ? pendingBtReq.reason === "brain_type_double_check"
                        ? t("dashboard.bt.pending_dc")
                        : t("dashboard.bt.pending_consult")
                      : t("dashboard.bt.preliminary")}
                  </p>
                  <p className="font-heading text-4xl text-[#1A1D1A] font-semibold mt-2">
                    #{preliminaryBt}
                  </p>
                  {btMeta && (
                    <p className="text-sm text-[#5C635A] mt-1">{btMeta.name}</p>
                  )}
                </div>
              ) : (
                <div data-testid="dashboard-bt-empty">
                  <p className="text-sm text-[#5C635A] leading-relaxed">
                    {t("dashboard.bt.no_definitive")}
                  </p>
                </div>
              )}

              {definitiveBt == null && (
                <div className="mt-5">
                  {pendingBtReq ? (
                    <div
                      className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-[#B37A5F]"
                      data-testid="dashboard-bt-pending-badge"
                    >
                      <CheckCircle2 strokeWidth={1.5} size={14} />
                      {t("brain_type.coach_notified_pending")}
                    </div>
                  ) : (
                    <Button
                      type="button"
                      onClick={requestBrainTypeCheck}
                      disabled={requestingBt}
                      data-testid="dashboard-bt-request-button"
                      className="h-10 rounded-lg bg-[#4A5D4E] hover:bg-[#3D4D41] text-white text-sm w-full sm:w-auto"
                    >
                      <Sparkles strokeWidth={1.5} size={16} className="mr-2" />
                      {requestingBt
                        ? t("brain_type.sending_request")
                        : preliminaryBt != null
                        ? t("bt_test.request_double_check")
                        : t("brain_type.request_consultation")}
                    </Button>
                  )}
                </div>
              )}

              <Link
                to="/me/brain-type"
                data-testid="dashboard-bt-link"
                className="mt-auto pt-5 text-sm font-bold uppercase tracking-[0.2em] text-[#4A5D4E] hover:text-[#3D4D41] flex items-center gap-2"
              >
                {t("dashboard.bt.link")}
                <ArrowRight strokeWidth={1.5} size={14} />
              </Link>
            </div>

            {/* Quality of Life summary */}
            <div
              className="bg-white border border-[#E5E2DC] rounded-2xl p-6 flex flex-col"
              data-testid="dashboard-qol-card"
            >
              <div className="flex items-center gap-3 mb-5">
                <div className="h-11 w-11 rounded-xl bg-[#F2F0EA] flex items-center justify-center">
                  <HeartPulse strokeWidth={1.5} className="text-[#B37A5F]" size={20} />
                </div>
                <h2 className="font-heading text-xl text-[#1A1D1A] tracking-tight font-semibold">
                  {t("dashboard.qol.title")}
                </h2>
              </div>

              {hasAnyQol ? (
                <ul className="space-y-2.5" data-testid="dashboard-qol-list">
                  {QOL_SUBJECTS.map(({ key, labelKey, icon: Icon }) => {
                    const val = qol[key];
                    const meta = val ? QOL_OPTION_META[val] : null;
                    const OptIcon = meta?.icon;
                    return (
                      <li
                        key={key}
                        className="flex items-center justify-between"
                        data-testid={`dashboard-qol-${key}`}
                      >
                        <span className="flex items-center gap-2 text-sm text-[#2A2D2A]">
                          <Icon strokeWidth={1.5} className="text-[#8A9188]" size={16} />
                          {t(labelKey)}
                        </span>
                        {meta ? (
                          <span
                            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold text-white"
                            style={{ backgroundColor: meta.color }}
                          >
                            <OptIcon strokeWidth={1.75} size={12} />
                            {t(meta.labelKey)}
                          </span>
                        ) : (
                          <span className="text-xs uppercase tracking-[0.15em] text-[#8A9188] font-bold">
                            {t("dashboard.qol.unset")}
                          </span>
                        )}
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <p className="text-sm text-[#5C635A] leading-relaxed">
                  {t("dashboard.qol.empty")}
                </p>
              )}

              {qol.updated_at && (
                <p className="text-[10px] uppercase tracking-[0.2em] text-[#8A9188] font-bold mt-4">
                  {t("dashboard.qol.updated")}: {formatDate(qol.updated_at)}
                </p>
              )}

              <Link
                to="/me/quality-of-life"
                data-testid="dashboard-qol-link"
                className="mt-auto pt-5 text-sm font-bold uppercase tracking-[0.2em] text-[#4A5D4E] hover:text-[#3D4D41] flex items-center gap-2"
              >
                {t("dashboard.qol.link")}
                <ArrowRight strokeWidth={1.5} size={14} />
              </Link>
            </div>

            {/* GOT Practice count */}
            <div
              className="bg-white border border-[#E5E2DC] rounded-2xl p-6 flex flex-col"
              data-testid="dashboard-got-card"
            >
              <div className="flex items-center gap-3 mb-5">
                <div className="h-11 w-11 rounded-xl bg-[#F2F0EA] flex items-center justify-center">
                  <MessagesSquare strokeWidth={1.5} className="text-[#B37A5F]" size={20} />
                </div>
                <h2 className="font-heading text-xl text-[#1A1D1A] tracking-tight font-semibold">
                  {t("dashboard.got.title")}
                </h2>
              </div>

              {gotStats.total === 0 ? (
                <p className="text-sm text-[#5C635A] leading-relaxed">
                  {t("dashboard.got.zero_state")}
                </p>
              ) : (
                <div data-testid="dashboard-got-stats">
                  <p className="font-heading text-5xl font-semibold text-[#1A1D1A] leading-none">
                    {gotStats.total}
                  </p>
                  <p className="text-sm text-[#5C635A] mt-2">
                    {gotStats.total === 1
                      ? t("dashboard.got.total_one")
                      : t("dashboard.got.total_many")}
                    <span className="text-[#8A9188]">
                      {" "}
                      · {gotStats.completed}{" "}
                      {gotStats.completed === 1
                        ? t("dashboard.got.completed_one")
                        : t("dashboard.got.completed_many")}
                    </span>
                  </p>
                </div>
              )}

              <Link
                to="/me/got-practice"
                data-testid="dashboard-got-link"
                className="mt-auto pt-5 text-sm font-bold uppercase tracking-[0.2em] text-[#4A5D4E] hover:text-[#3D4D41] flex items-center gap-2"
              >
                <Sparkles strokeWidth={1.5} size={14} />
                {t("dashboard.got.link")}
              </Link>
            </div>
          </div>
        </div>
      )}

      {showCheckIn && (
        <QolCheckInModal
          initialState={qol}
          onStateChange={(next) =>
            setQol({
              feel: next.feel ?? null,
              past: next.past ?? null,
              future: next.future ?? null,
              inner_self_talk: next.inner_self_talk ?? null,
              updated_at: next.updated_at ?? qol.updated_at,
            })
          }
          onClose={dismissCheckIn}
        />
      )}
    </div>
  );
}
